import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";

// ===== MODULE-LEVEL CONSTANTS & UTILITIES =====

// Pac-Man constants
const PAC_CELL = 18;
const PAC_COLS = 19;
const PAC_ROWS = 21;
const PAC_MAZE_TPL = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,0,1],
  [1,3,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,3,1],
  [1,0,1,1,0,1,0,1,1,1,1,1,0,1,0,1,1,0,1],
  [1,0,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0,0,1],
  [1,1,1,1,0,1,1,1,4,1,4,1,1,1,0,1,1,1,1],
  [4,4,4,1,0,1,4,4,4,4,4,4,4,1,0,1,4,4,4],
  [1,1,1,1,0,1,4,1,1,4,1,1,4,1,0,1,1,1,1],
  [4,4,4,4,0,4,4,1,4,4,4,1,4,4,0,4,4,4,4],
  [1,1,1,1,0,1,4,1,1,1,1,1,4,1,0,1,1,1,1],
  [4,4,4,1,0,1,4,4,4,4,4,4,4,1,0,1,4,4,4],
  [1,1,1,1,0,1,4,1,1,1,1,1,4,1,0,1,1,1,1],
  [1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,0,1],
  [1,3,0,1,0,0,0,0,0,4,0,0,0,0,0,1,0,3,1],
  [1,1,0,1,0,1,0,1,1,1,1,1,0,1,0,1,0,1,1],
  [1,0,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0,0,1],
  [1,0,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const getDelta = (dir) => {
  const m = { up:{dx:0,dy:-1}, down:{dx:0,dy:1}, left:{dx:-1,dy:0}, right:{dx:1,dy:0} };
  return m[dir] || {dx:0,dy:0};
};
const oppDir = (d) => ({up:'down',down:'up',left:'right',right:'left'}[d]);
const pacIsWall = (maze, x, y) => {
  if (y === 9 && (x < 0 || x >= PAC_COLS)) return false;
  if (x < 0 || x >= PAC_COLS || y < 0 || y >= PAC_ROWS) return true;
  return maze[y][x] === 1;
};

// Ghost initial positions by index
const GHOST_START_X = [9, 8, 10, 9];
const GHOST_START_Y = [7, 9, 9, 9];

// Get ghost fill color based on state
const getGhostColor = (ghost, scared, blink) => {
  if (!scared) return ghost.color;
  return blink ? '#fff' : '#223366';
};

// Get ghost eye color based on state
const getGhostEyeColor = (scared, blink) => {
  if (!scared) return '#fff';
  return blink ? '#f00' : '#fff';
};

// Get minesweeper cell content
const getMinesweeperCellContent = (cell, getNumberColor) => {
  if (!cell.isRevealed) return cell.isFlagged ? '🚩' : null;
  if (cell.isMine) return '💣';
  if (cell.neighborMines > 0) return <span className={getNumberColor(cell.neighborMines)}>{cell.neighborMines}</span>;
  return null;
};

// Get minesweeper face emoji
const getMinesweeperFace = (gameOver, gameWon) => {
  if (gameOver) return '😵';
  if (gameWon) return '😎';
  return '🙂';
};

// ===== PAC-MAN GAME LOGIC HELPERS =====

// Move a ghost toward target or randomly when scared
const moveGhost = (g, pac, maze, scared) => {
  if (!g.released) return;
  const dirs = ['up','down','left','right'].filter(d => {
    if (d === oppDir(g.dir)) return false;
    const dd = getDelta(d);
    return !pacIsWall(maze, g.x + dd.dx, g.y + dd.dy);
  });
  if (dirs.length === 0) dirs.push(oppDir(g.dir));

  let best;
  if (scared) {
    best = dirs[Math.floor(Math.random() * dirs.length)];
  } else {
    let bd = Infinity;
    dirs.forEach(d => {
      const dd = getDelta(d);
      const dist = Math.abs(g.x + dd.dx - pac.x) + Math.abs(g.y + dd.dy - pac.y);
      if (dist < bd) { bd = dist; best = d; }
    });
  }
  const dd = getDelta(best);
  g.x += dd.dx; g.y += dd.dy; g.dir = best;
  if (g.x < 0) g.x = PAC_COLS - 1;
  if (g.x >= PAC_COLS) g.x = 0;
};

// Move pac-man and eat pills, returns 'win' | 'ate_power' | 'moved' | 'blocked'
const movePacman = (pac, maze) => {
  const nd = getDelta(pac.nextDir);
  if (!pacIsWall(maze, pac.x + nd.dx, pac.y + nd.dy)) pac.dir = pac.nextDir;
  const cd = getDelta(pac.dir);
  const nx = pac.x + cd.dx, ny = pac.y + cd.dy;
  if (pacIsWall(maze, nx, ny)) return 'blocked';

  pac.x = nx; pac.y = ny;
  if (pac.x < 0) pac.x = PAC_COLS - 1;
  if (pac.x >= PAC_COLS) pac.x = 0;
  return 'moved';
};

// Reset ghost positions after pac-man death
const resetGhostPositions = (ghosts, tick) => {
  ghosts.forEach((gg, gi) => {
    gg.x = GHOST_START_X[gi]; gg.y = GHOST_START_Y[gi];
    gg.dir = 'up'; gg.released = gi === 0;
    if (!gg.released) gg.releaseAt = tick + 20 * gi;
  });
};

// Draw maze cells on canvas
const drawMazeCell = (ctx, cell, x, y, mouth) => {
  if (cell === 1) {
    ctx.fillStyle = '#1a4d1a';
    ctx.fillRect(x + 1, y + 1, PAC_CELL - 2, PAC_CELL - 2);
    ctx.strokeStyle = '#83bf68';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 1.5, y + 1.5, PAC_CELL - 3, PAC_CELL - 3);
  } else if (cell === 0) {
    ctx.fillStyle = '#83bf68';
    ctx.beginPath();
    ctx.ellipse(x + PAC_CELL / 2, y + PAC_CELL / 2, 3, 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#4a7a3a';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x + PAC_CELL / 2, y + PAC_CELL / 2 - 2);
    ctx.lineTo(x + PAC_CELL / 2, y + PAC_CELL / 2 + 2);
    ctx.stroke();
  } else if (cell === 3) {
    const pulse = 4 + Math.sin((mouth || 0) * 2) * 2;
    ctx.fillStyle = '#83bf68';
    ctx.beginPath();
    ctx.ellipse(x + PAC_CELL / 2, y + PAC_CELL / 2, pulse, pulse * 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#4a7a3a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + PAC_CELL / 2, y + PAC_CELL / 2 - pulse * 0.7);
    ctx.lineTo(x + PAC_CELL / 2, y + PAC_CELL / 2 + pulse * 0.7);
    ctx.stroke();
  }
};

// Draw a police ghost on canvas
const drawGhost = (ctx, g, scared, tick) => {
  if (g.x < 0 || g.x >= PAC_COLS) return;
  const cx = g.x * PAC_CELL + PAC_CELL / 2, cy = g.y * PAC_CELL + PAC_CELL / 2;
  const blink = scared && tick < 10 && (Math.floor(Date.now() / 250)) % 2 === 0;

  ctx.fillStyle = getGhostColor(g, scared, blink);
  ctx.beginPath();
  ctx.arc(cx, cy - 1, 7, Math.PI, 0);
  ctx.lineTo(cx + 7, cy + 7);
  for (let i = 3; i >= -3; i--) ctx.lineTo(cx + i * 2.3, cy + 7 + (i % 2 ? -3 : 0));
  ctx.closePath();
  ctx.fill();

  if (!scared) {
    ctx.fillStyle = '#111';
    ctx.fillRect(cx - 8, cy - 8, 16, 3);
    ctx.fillRect(cx - 6, cy - 11, 12, 3);
    ctx.fillStyle = '#ffd700';
    ctx.fillRect(cx - 2, cy - 10, 4, 2);
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    ctx.moveTo(cx, cy + 1); ctx.lineTo(cx - 2, cy + 3); ctx.lineTo(cx, cy + 5); ctx.lineTo(cx + 2, cy + 3);
    ctx.closePath();
    ctx.fill();
  }

  ctx.fillStyle = getGhostEyeColor(scared, blink);
  ctx.fillRect(cx - 5, cy - 4, 4, 5);
  ctx.fillRect(cx + 1, cy - 4, 4, 5);
  if (!scared) {
    ctx.fillStyle = '#000';
    const pd = getDelta(g.dir);
    ctx.fillRect(cx - 4 + pd.dx * 2, cy - 3 + pd.dy * 2, 2, 3);
    ctx.fillRect(cx + 2 + pd.dx * 2, cy - 3 + pd.dy * 2, 2, 3);
  }
};

// Draw pac-man on canvas
const drawPacman = (ctx, pac, mouth) => {
  const px = pac.x * PAC_CELL + PAC_CELL / 2, py = pac.y * PAC_CELL + PAC_CELL / 2;
  const ma = Math.abs(Math.sin(mouth || 0)) * 0.8;
  ctx.fillStyle = '#ffff00';
  ctx.beginPath();
  let sa = 0, ea = 0;
  switch (pac.dir) {
    case 'right': sa = ma; ea = Math.PI * 2 - ma; break;
    case 'left': sa = Math.PI + ma; ea = Math.PI - ma; break;
    case 'up': sa = Math.PI * 1.5 + ma; ea = Math.PI * 1.5 - ma; break;
    case 'down': sa = Math.PI * 0.5 + ma; ea = Math.PI * 0.5 - ma; break;
    default: sa = ma; ea = Math.PI * 2 - ma;
  }
  ctx.moveTo(px, py);
  ctx.arc(px, py, 8, sa, ea);
  ctx.closePath();
  ctx.fill();
};

// Snake constants
const SNAKE_GRID_SIZE = 20;
const SNAKE_CELL_SIZE = 20;
const SNAKE_SPEED = 150;

// Minesweeper constants
const MINE_ROWS = 16;
const MINE_COLS = 16;
const MINE_COUNT = 40;

// Typewriter Hook
const useTypewriter = (text, speed = 30, startTyping = true) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!startTyping) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    let index = 0;

    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        setIsComplete(true);
        clearInterval(timer);
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, startTyping]);

  return { displayedText, isComplete };
};

// Typewriter Text Component
const TypewriterText = ({ text, speed = 30, className = '' }) => {
  const { displayedText } = useTypewriter(text, speed);
  return (
    <span className={className}>
      {displayedText}
      <span className="animate-blink">_</span>
    </span>
  );
};

// Scanlines Overlay Component
const Scanlines = () =>
<div className="scanlines fixed inset-0 pointer-events-none z-50" />;


// Desktop Folder Component
const DesktopFolder = ({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="flex flex-col items-center cursor-pointer group"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="desktop-folder">

      <div className={`relative transition-transform duration-100 ${isHovered ? 'scale-105' : ''}`}>
        <img
          src="/assets/folder.png"
          alt="Folder"
          className={`w-32 h-32 md:w-48 md:h-48 ${isHovered ? 'animate-glitch' : ''}`}
          style={{ imageRendering: 'pixelated' }} />

      </div>
      <h1
        className={`mt-6 text-xl md:text-3xl font-bold text-retro-green uppercase tracking-widest text-center ${isHovered ? 'glitch-text' : ''}`}
        data-text="Il Senso Del Caos">

        Il Senso Del Caos
      </h1>
    </div>);

};

// Retro Window Component (uses CORNICE.png)
const RetroWindow = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-40 bg-retro-black md:flex md:items-center md:justify-center md:p-8"
      data-testid="retro-window">

      {/* Mobile layout */}
      <div className="md:hidden h-full w-full flex flex-col">
        {/* Mobile title bar - fixed */}
        <div className="h-10 bg-retro-green flex items-center justify-end px-2 flex-shrink-0 border-b-4 border-retro-green">
          <button
            onClick={onClose}
            className="text-retro-black font-bold text-lg px-2 hover:bg-retro-dim-green"
            data-testid="close-window-btn-mobile"
            aria-label="Chiudi">
            X
          </button>
        </div>
        {/* Mobile content - scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 border-4 border-t-0 border-retro-green" style={{ WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:block relative w-full max-w-4xl h-[85vh]">
        {/* Window Frame using CORNICE.png */}
        <div
          className="absolute inset-0 bg-contain bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/CORNICE.png)',
            backgroundSize: '100% 100%'
          }} />
        
        {/* Close Button for desktop */}
        <button
          onClick={onClose}
          className="absolute top-[1.8%] right-[2.5%] md:top-[3.4%] md:right-[2.8%] md:w-[2.8%] md:h-[4.2%] flex items-center justify-center z-10 !font-sans !font-normal !text-xl !leading-none text-retro-green hover:scale-110 transition-transform duration-150"
          data-testid="close-window-btn"
          aria-label="Chiudi">
          X
        </button>
        
        {/* Content Area */}
        <div className="absolute inset-[4%] top-[10%] overflow-hidden">
          <div className="h-full overflow-auto p-8">
            {children}
          </div>
        </div>
      </div>
    </div>);

};

// Menu Folder Item
const MenuFolder = ({ name, onClick, isActive }) =>
<button
  onClick={onClick}
  className={`flex items-center gap-4 p-3 md:p-4 w-full text-left uppercase tracking-wider text-base md:text-xl transition-colors ${
  isActive ?
  'bg-retro-green text-retro-black' :
  'text-retro-green hover:bg-retro-green hover:text-retro-black'}`
  }
  data-testid={`menu-${name.toLowerCase()}`}>

    <img
    src="/assets/folder.png"
    alt="Folder"
    className="w-8 h-8 md:w-12 md:h-12"
    style={{ imageRendering: 'pixelated' }} />

    <span className="font-matrix font-bold">{name}</span>
  </button>;


// Accordion Item for Concept page with Typewriter
const ConceptAccordion = ({ title, iconText, description }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { displayedText: iconDisplayed, isComplete: iconComplete } = useTypewriter(iconText, 25, isOpen);
  const { displayedText: descDisplayed } = useTypewriter(description, 15, isOpen && iconComplete);

  return (
    <div className="border-2 border-retro-green">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 md:p-6 text-left flex items-center hover:bg-retro-green hover:bg-opacity-10 transition-colors"
        data-testid={`accordion-${title.toLowerCase().replace(/[^a-z]/g, '')}`}>

        <span
          className="text-lg md:text-2xl font-bold mr-2 inline-block transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>

          &gt;
        </span>
        <h3 className="text-lg md:text-2xl font-bold uppercase">{title}</h3>
      </button>
      {isOpen &&
      <div className="p-4 md:p-6 pt-0 border-t border-retro-green">
          <p className="text-retro-dim-green mb-2">
            {iconDisplayed}
            {!iconComplete && <span className="animate-blink">_</span>}
          </p>
          {iconComplete && (
            <p>
              {descDisplayed}
              <span className="animate-blink">_</span>
            </p>
          )}
        </div>
      }
    </div>);

};

// Concept Page Content
const ConceptPage = () => {
  const text1 = "L'album non e' una celebrazione del disordine, ma la documentazione scientifica di un segnale che si frantuma nel tentativo di farsi capire: una mappatura rigorosa di un corto circuito dove l'interferenza smette di essere rumore per diventare l'unico vero messaggio.";
  const text2 = "Abbandonando l'estetica dell'artista tormentato per quella dell'uomo come laboratorio, il progetto analizza il bug comunicativo nel suo percorso dall'impulso cerebrale all'archiviazione oculare, fino alla traduzione manuale.\nQui, l'errore non viene corretto, ma adottato come frequenza necessaria per dire la verita': un codice interno che la mano trasmette e che il mondo e' costretto a decifrare per poter leggere.";

  return (
    <div className="space-y-8 text-retro-green font-matrix" data-testid="concept-page">
      <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="CONCEPT">
        CONCEPT
      </h2>
      
      <div className="space-y-6 text-sm md:text-base leading-relaxed">
        <p>{text1}</p>
        
        <p>{text2}</p>
        
        <div className="space-y-4 mt-8">
          <ConceptAccordion 
            title="INPUT [IMPULSO]"
            iconText="ICONOGRAFIA = CERVELLO"
            description="Energia umana grezza e verita' dal cervello, non mediata e originata dal disordine creativo, punto di partenza dell'attivita' elettrica"
          />
          
          <ConceptAccordion 
            title="VISIONE [SISTEMA]"
            iconText="ICONOGRAFIA = OCCHI"
            description="Una griglia che archivia il caos, necessaria per osservare il corto circuito ed il filtro che cattura e deforma il segnale"
          />
          
          <ConceptAccordion 
            title="OUTPUT [CODICE]"
            iconText="ICONOGRAFIA = MANO"
            description="Lingua finale e alfabeto personale che costringe a decifrare il canale che traduce il disordine in un codice privato"
          />
        </div>
      </div>
    </div>
  );
};
const AudioPage = () => <div className="space-y-8 text-retro-green font-matrix" data-testid="audio-page">
    <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="AUDIO">
      AUDIO
    </h2>
    
    <p className="text-sm md:text-base text-retro-dim-green uppercase tracking-wide">
      &gt; LOADING AUDIO FILES...
    </p>
    
    <div className="w-full border-2 border-retro-green p-2">
      <iframe title="Il Senso Del Caos - Spotify" src="https://open.spotify.com/embed/album/1waUySAZZZYdFHaANpmeXL"
      width="100%"
      height="352"
      frameBorder="0"
      allowFullScreen
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      className="bg-retro-black"
      style={{ borderRadius: 0, backgroundColor: '#070301' }}
      data-testid="spotify-embed" />

    </div>
    
    <p className="text-xs text-retro-dim-green uppercase">
      &gt; STREAMING ACTIVE_
    </p>
  </div>;


// Merchandising Page Content
const MerchandisingPage = () => {
  const products = [
    { id: 'cd', src: '/assets/cd_cover.png', name: 'CD "IL SENSO DEL CAOS"', desc: 'Rico Rua - Album Fisico' },
    { id: 'photo', src: '/assets/tshirt_photo.png', name: 'T-SHIRT "PHOTO"', desc: 'Il Senso Del Caos - Back Print' },
    { id: 'pixel', src: '/assets/tshirt_pixel.png', name: 'T-SHIRT "PIXEL"', desc: 'Il Senso Del Caos - Pixel Art' },
  ];

  return (
    <div className="space-y-8 text-retro-green font-matrix" data-testid="merchandising-page">
      <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="MERCHANDISING">
        MERCHANDISING
      </h2>

      <p className="text-sm text-retro-dim-green uppercase">
        &gt; AVAILABLE ITEMS_
      </p>

      <div className="space-y-6">
        {/* CD - full width on top */}
        <div className="border-2 border-retro-green bg-retro-black group" data-testid="merch-cd">
          <div className="overflow-hidden max-w-sm mx-auto">
            <img
              src={products[0].src}
              alt={products[0].name}
              className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
            />
          </div>
          <div className="p-4 border-t-2 border-retro-green space-y-3">
            <h3 className="text-lg font-bold uppercase tracking-wider text-center">{products[0].name}</h3>
            <p className="text-xs text-retro-dim-green text-center">{products[0].desc}</p>
            <a
              href="https://www.instagram.com/ricorua47/"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-retro-green text-retro-black px-4 py-2 font-bold uppercase tracking-wider hover:bg-retro-dim-green transition-colors"
              data-testid="buy-cd"
            >
              ACQUISTA VIA INSTAGRAM
            </a>
          </div>
        </div>

        {/* T-Shirts - 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.slice(1).map((product) => (
            <div key={product.id} className="border-2 border-retro-green bg-retro-black group" data-testid={`merch-${product.id}`}>
              <div className="overflow-hidden">
                <img
                  src={product.src}
                  alt={product.name}
                  className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-4 border-t-2 border-retro-green space-y-3">
                <h3 className="text-lg font-bold uppercase tracking-wider">{product.name}</h3>
                <p className="text-xs text-retro-dim-green">{product.desc}</p>
                <a
                  href="https://www.instagram.com/ricorua47/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-retro-green text-retro-black px-4 py-2 font-bold uppercase tracking-wider hover:bg-retro-dim-green transition-colors"
                  data-testid={`buy-${product.id}`}
                >
                  ACQUISTA VIA INSTAGRAM
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center text-xs text-retro-dim-green border border-retro-dim-green p-3">
        <p>&gt; PER ACQUISTARE SCRIVI IN DM SU INSTAGRAM @ricorua47</p>
      </div>
    </div>
  );
};


// Contact Page Content
const ContactPage = () =>
<div className="space-y-8 text-retro-green font-matrix" data-testid="contact-page">
    <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="CONTACT">
      CONTACT
    </h2>
    
    <div className="space-y-6">
      <div className="border-2 border-retro-green p-4 md:p-6">
        <p className="text-retro-dim-green text-sm uppercase mb-2">&gt; EMAIL:</p>
        <a
        href="mailto:ricoruaofficial@gmail.com"
        className="text-sm md:text-xl hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors inline-block break-all"
        data-testid="contact-email">

          ricoruaofficial@gmail.com
        </a>
      </div>
      
      <div className="border-2 border-retro-green p-4 md:p-6">
        <p className="text-retro-dim-green text-sm uppercase mb-4">&gt; SOCIAL LINKS:</p>
        
        <div className="space-y-4">
          <a
          href="https://www.instagram.com/ricorua47/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 text-lg md:text-xl hover:bg-retro-green hover:text-retro-black px-2 py-2 transition-colors group"
          data-testid="contact-instagram">

            <span className="text-retro-dim-green group-hover:text-retro-black">[01]</span>
            <span>INSTAGRAM</span>
            <span className="text-retro-dim-green group-hover:text-retro-black text-sm">@ricorua47</span>
          </a>
          
          <a
          href="https://open.spotify.com/intl-it/artist/5qtpAedIGNUyxyAyZ4B74E?si=jij3HM7pQDKVU5JPzewjcA"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 text-lg md:text-xl hover:bg-retro-green hover:text-retro-black px-2 py-2 transition-colors group"
          data-testid="contact-spotify">

            <span className="text-retro-dim-green group-hover:text-retro-black">[02]</span>
            <span>SPOTIFY</span>
            <span className="text-retro-dim-green group-hover:text-retro-black text-sm">Rico Rua</span>
          </a>
        </div>
      </div>
      
      <p className="text-xs text-retro-dim-green uppercase">
        &gt; CONNECTION_STATUS: ACTIVE_
      </p>
    </div>
  </div>;


// Extra Page Content with Submenu
const ExtraPage = ({ onNavigate }) =>
<div className="space-y-8 text-retro-green font-matrix" data-testid="extra-page">
    <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="EXTRA">
      EXTRA
    </h2>
    
    <div className="space-y-4">
      <p className="text-sm text-retro-dim-green uppercase mb-6">
        &gt; SELECT SUBFOLDER_
      </p>
      
      <button
        onClick={() => onNavigate('artworks')}
        className="flex items-center gap-4 p-3 md:p-4 w-full text-left uppercase tracking-wider text-base md:text-xl transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black"
        data-testid="menu-artworks"
      >
        <img 
          src="/assets/folder.png" 
          alt="Folder" 
          className="w-8 h-8 md:w-12 md:h-12"
          style={{ imageRendering: 'pixelated' }}
        />
        <span className="font-matrix font-bold">Artworks</span>
      </button>
      
      <button
        onClick={() => onNavigate('content')}
        className="flex items-center gap-4 p-3 md:p-4 w-full text-left uppercase tracking-wider text-base md:text-xl transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black"
        data-testid="menu-content"
      >
        <img 
          src="/assets/folder.png" 
          alt="Folder" 
          className="w-8 h-8 md:w-12 md:h-12"
          style={{ imageRendering: 'pixelated' }}
        />
        <span className="font-matrix font-bold">Content</span>
      </button>

      <button
        onClick={() => onNavigate('download')}
        className="flex items-center gap-4 p-3 md:p-4 w-full text-left uppercase tracking-wider text-base md:text-xl transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black"
        data-testid="menu-download"
      >
        <img 
          src="/assets/folder.png" 
          alt="Folder" 
          className="w-8 h-8 md:w-12 md:h-12"
          style={{ imageRendering: 'pixelated' }}
        />
        <span className="font-matrix font-bold">Download</span>
      </button>
      
      <button
        onClick={() => onNavigate('games')}
        className="flex items-center gap-4 p-3 md:p-4 w-full text-left uppercase tracking-wider text-base md:text-xl transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black"
        data-testid="menu-games"
      >
        <img 
          src="/assets/folder.png" 
          alt="Folder" 
          className="w-8 h-8 md:w-12 md:h-12"
          style={{ imageRendering: 'pixelated' }}
        />
        <span className="font-matrix font-bold">Games</span>
      </button>
    </div>
  </div>;


// Artworks Page Content
const ArtworksPage = () => {
  const [expandedImg, setExpandedImg] = useState(null);
  
  const artworks = [
    { src: '/assets/artwork_cover.png', title: 'IL SENSO DEL CAOS' },
    { src: '/assets/artwork_tsr.png', title: 'TSR' },
    { src: '/assets/artwork1.png', title: 'TU MI FAI' },
    { src: '/assets/artwork2.png', title: 'TECHWEAR' },
  ];

  // Generate matrix rain columns
  const matrixColumns = useRef(
    Array.from({ length: 14 }, (_, i) => ({
      left: `${(i / 14) * 100 + Math.random() * 5}%`,
      chars: Array.from({ length: 12 + Math.floor(Math.random() * 10) }, () =>
        String.fromCharCode(0x30A0 + Math.floor(Math.random() * 96))
      ).join(''),
      duration: `${4 + Math.random() * 6}s`,
      delay: `${Math.random() * 5}s`,
      opacity: 0.08 + Math.random() * 0.12,
    }))
  ).current;
  
  return (
    <div className="space-y-8 text-retro-green font-matrix" data-testid="artworks-page">
      <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="ARTWORKS">
        ARTWORKS
      </h2>
      
      <p className="text-sm text-retro-dim-green uppercase">
        &gt; COVER ART FILES_
      </p>
      
      <div className="grid grid-cols-2 gap-4">
        {artworks.map((art) => (
          <div
            key={art.title}
            className="border-2 border-retro-green overflow-hidden cursor-pointer group"
            onClick={() => setExpandedImg(art)}
            data-testid={`artwork-${art.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="overflow-hidden">
              <img
                src={art.src}
                alt={art.title}
                className="w-full h-auto transition-transform duration-300 group-hover:scale-150"
              />
            </div>
            <p className="text-xs text-retro-dim-green uppercase text-center py-2 border-t border-retro-green">
              {art.title}
            </p>
          </div>
        ))}
      </div>
      
      {/* Glitch Lightbox */}
      {expandedImg && (
        <div
          className="fixed inset-0 bg-retro-black z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedImg(null)}
          data-testid="artwork-lightbox"
        >
          {/* Matrix Rain Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ opacity: 0.6 }}>
            {matrixColumns.map((col) => (
              <div
                key={col.left}
                className="matrix-column"
                style={{
                  left: col.left,
                  '--fall-duration': col.duration,
                  '--fall-delay': col.delay,
                  opacity: col.opacity,
                  fontSize: '10px',
                }}
              >
                {col.chars}
              </div>
            ))}
          </div>

          <div className="relative max-w-3xl w-full lightbox-flicker" onClick={(e) => e.stopPropagation()}>
            {/* Main Image */}
            <div className="relative overflow-hidden border-2 border-retro-green">
              <img
                src={expandedImg.src}
                alt={expandedImg.title}
                className="w-full h-auto block"
              />
              
              {/* RGB Ghost layers */}
              <div className="rgb-ghost-r">
                <img src={expandedImg.src} alt="" className="w-full h-auto block" />
              </div>
              <div className="rgb-ghost-b">
                <img src={expandedImg.src} alt="" className="w-full h-auto block" />
              </div>
              
              {/* Scanlines overlay */}
              <div className="lightbox-scanlines" />
              
              {/* Glitch bars */}
              <div className="glitch-bars">
                <div className="glitch-bar" />
                <div className="glitch-bar" />
                <div className="glitch-bar" />
              </div>
              
              {/* Static noise */}
              <div className="static-overlay" />
            </div>
            
            {/* Title with glitch */}
            <p className="text-center text-retro-green uppercase tracking-widest mt-4 text-sm glitch-text" data-text={expandedImg.title}>
              {expandedImg.title}
            </p>
            
            <button
              onClick={() => setExpandedImg(null)}
              className="absolute -top-10 right-0 text-retro-green hover:text-retro-dim-green text-sm uppercase"
              data-testid="close-lightbox"
            >
              [ CHIUDI ]
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// Content Page - Carousel
const ContentPage = () => {
  const items = [
    { src: '/assets/content_testa.png', title: 'TESTA' },
    { src: '/assets/content_finestra_scritta.png', title: 'FINESTRA_SCRITTA' },
    { src: '/assets/content_finestra_rete.png', title: 'FINESTRA_RETE' },
    { src: '/assets/content_alien.png', title: 'ALIEN' },
  ];

  const [idx, setIdx] = useState(0);
  const total = items.length;

  const goPrev = () => setIdx((i) => (i - 1 + total) % total);
  const goNext = () => setIdx((i) => (i + 1) % total);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = items[idx];
  const counter = `[${String(idx + 1).padStart(2, '0')}/${String(total).padStart(2, '0')}]`;

  return (
    <div className="space-y-6 text-retro-green font-matrix" data-testid="content-page">
      <h2
        className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text"
        data-text="CONTENT"
      >
        CONTENT
      </h2>

      <p className="text-sm text-retro-dim-green uppercase">
        &gt; VISUAL FILES_ {counter}
      </p>

      <div className="relative overflow-hidden bg-retro-black">
        {/* Image */}
        <div className="w-full aspect-square flex items-center justify-center overflow-hidden">
          <img
            key={current.src}
            src={current.src}
            alt={current.title}
            className="w-full h-full object-contain"
            style={{ imageRendering: 'pixelated' }}
            data-testid={`content-img-${idx}`}
          />
        </div>

        {/* Scanline overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, rgba(131,191,104,0.15) 0px, rgba(131,191,104,0.15) 1px, transparent 1px, transparent 3px)',
          }}
        />

        {/* Prev / Next arrows */}
        <button
          onClick={goPrev}
          aria-label="Previous"
          className="absolute left-2 top-1/2 -translate-y-1/2 text-2xl md:text-4xl px-3 py-1 md:px-4 md:py-2 border-2 border-retro-green bg-retro-black text-retro-green hover:bg-retro-green hover:text-retro-black transition-colors font-bold"
          data-testid="content-prev"
        >
          &lt;
        </button>
        <button
          onClick={goNext}
          aria-label="Next"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-2xl md:text-4xl px-3 py-1 md:px-4 md:py-2 border-2 border-retro-green bg-retro-black text-retro-green hover:bg-retro-green hover:text-retro-black transition-colors font-bold"
          data-testid="content-next"
        >
          &gt;
        </button>
      </div>

      {/* Caption + dot indicators */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className="text-base md:text-lg uppercase tracking-widest">
          &gt; {current.title}_
        </p>

        <div className="flex items-center gap-3" data-testid="content-dots">
          {items.map((it, i) => (
            <button
              key={it.title}
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-3 h-3 md:w-4 md:h-4 border-2 border-retro-green transition-colors ${
                i === idx ? 'bg-retro-green' : 'bg-transparent hover:bg-retro-dim-green'
              }`}
              data-testid={`content-dot-${i}`}
            />
          ))}
        </div>
      </div>

      <p className="text-xs text-retro-dim-green uppercase">
        &gt; USE ARROWS OR CLICK_
      </p>
    </div>
  );
};

// Download Page Content
const DownloadPage = () =>
<div className="space-y-8 text-retro-green font-matrix" data-testid="download-page">
    <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="DOWNLOAD">
      DOWNLOAD
    </h2>
    
    <p className="text-sm text-retro-dim-green uppercase">
      &gt; ACAPELLA FILES FOR REMIX_
    </p>
    
    <div className="space-y-6">
      <div className="border-2 border-retro-green p-4 md:p-6">
        <p className="text-retro-dim-green text-sm uppercase mb-4">&gt; AVAILABLE FILES:</p>
        
        <a 
          href="https://drive.google.com/drive/folders/1ui4NzwEnor6XWgP2jq_DCIhVpq1j9Qo_"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 text-base md:text-xl hover:bg-retro-green hover:text-retro-black px-2 py-3 transition-colors group"
          data-testid="download-acapella"
        >
          <span className="text-retro-dim-green group-hover:text-retro-black">[01]</span>
          <span>VOCI ACAPELLA</span>
          <span className="text-retro-dim-green group-hover:text-retro-black text-sm ml-auto">GOOGLE DRIVE</span>
        </a>
      </div>
      
      <p className="text-xs text-retro-dim-green uppercase">
        &gt; DOWNLOAD_STATUS: ACTIVE_
      </p>
      <p className="text-xs text-retro-dim-green">
        Scarica le voci acapella per creare i tuoi remix
      </p>
    </div>
  </div>;


// Unreleased Track Popup Component
const UnreleasedTrackPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-retro-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="bg-retro-black border-4 border-retro-green p-6 max-w-md w-full text-center">
        <h3 className="text-2xl font-bold text-retro-green mb-4 uppercase tracking-widest animate-blink">
          TRACK SBLOCCATA!
        </h3>
        <p className="text-retro-dim-green mb-6 text-sm">
          Hai sbloccato una traccia unreleased esclusiva
        </p>
        
        <div className="space-y-4">
          <a
            href="https://youtu.be/Os2qxQw524I"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-retro-green text-retro-black py-3 px-4 font-bold uppercase hover:bg-retro-dim-green transition-colors"
          >
            ASCOLTA SU YOUTUBE
          </a>
        </div>
        
        <button
          onClick={onClose}
          className="mt-6 text-retro-dim-green text-sm uppercase hover:text-retro-green transition-colors"
        >
          [ CHIUDI ]
        </button>
      </div>
    </div>
  );
};

// Pac-Man Game Component
const PacmanGame = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef(null);

  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUnlocked, setShowUnlocked] = useState(false);

  const initState = useCallback(() => {
    const maze = PAC_MAZE_TPL.map(r => [...r]);
    let pills = 0;
    for (let r = 0; r < PAC_ROWS; r++)
      for (let c = 0; c < PAC_COLS; c++)
        if (maze[r][c] === 0 || maze[r][c] === 3) pills++;
    return {
      maze, pills, score: 0, lives: 3, scared: 0, tick: 0, mouth: 0, over: false, won: false,
      pac: { x: 9, y: 15, dir: 'left', nextDir: 'left' },
      ghosts: [
        { x: 9, y: 7, dir: 'left', color: '#ff3333', released: true, releaseAt: 0 },
        { x: 8, y: 9, dir: 'up', color: '#33aaff', released: false, releaseAt: 25 },
        { x: 10, y: 9, dir: 'up', color: '#ff66aa', released: false, releaseAt: 50 },
        { x: 9, y: 10, dir: 'up', color: '#ffaa33', released: false, releaseAt: 75 },
      ],
    };
  }, []);

  const resetGame = useCallback(() => {
    stateRef.current = initState();
    setScore(0); setLives(3); setGameOver(false); setIsPlaying(true); setShowUnlocked(false);
  }, [initState]);

  const changeDirection = useCallback((dir) => {
    if (stateRef.current) stateRef.current.pac.nextDir = dir;
  }, []);

  useEffect(() => {
    const h = (e) => {
      const map = { ArrowUp:'up', ArrowDown:'down', ArrowLeft:'left', ArrowRight:'right' };
      if (map[e.key]) { e.preventDefault(); changeDirection(map[e.key]); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [changeDirection]);

  useEffect(() => {
    if (!isPlaying || gameOver) return;
    const tick = () => {
      const s = stateRef.current;
      if (!s || s.over || s.won) return;
      s.tick++;
      s.mouth += 0.3;

      // Release ghosts
      s.ghosts.forEach(g => {
        if (!g.released && s.tick >= g.releaseAt) {
          g.released = true; g.x = 9; g.y = 7; g.dir = 'left';
        }
      });
      if (s.scared > 0) s.scared--;

      // Move pac-man
      const p = s.pac;
      const moveResult = movePacman(p, s.maze);
      if (moveResult !== 'blocked') {
        const cell = s.maze[p.y]?.[p.x];
        if (cell === 0) { s.maze[p.y][p.x] = 2; s.score += 10; s.pills--; }
        else if (cell === 3) {
          s.maze[p.y][p.x] = 2; s.score += 50; s.pills--; s.scared = 40;
          s.ghosts.forEach(g => { if (g.released) g.dir = oppDir(g.dir); });
        }
        if (s.pills <= 0) {
          s.won = true; setScore(s.score); setGameOver(true); setIsPlaying(false);
          setTimeout(() => setShowUnlocked(true), 1000); return;
        }
      }

      // Move ghosts (skip every other tick when scared)
      s.ghosts.forEach(g => {
        if (s.scared > 0 && s.tick % 2 === 0) return;
        moveGhost(g, p, s.maze, s.scared > 0);
      });

      // Check collisions
      s.ghosts.forEach(g => {
        if (!g.released || g.x !== p.x || g.y !== p.y) return;
        if (s.scared > 0) {
          s.score += 200; g.x = 9; g.y = 9; g.released = false; g.releaseAt = s.tick + 30;
        } else {
          s.lives--;
          if (s.lives <= 0) {
            s.over = true; setScore(s.score); setLives(0); setGameOver(true); setIsPlaying(false);
            setTimeout(() => setShowUnlocked(true), 1000);
          } else {
            p.x = 9; p.y = 15; p.dir = 'left'; p.nextDir = 'left';
            resetGhostPositions(s.ghosts, s.tick);
            setLives(s.lives);
          }
        }
      });
      setScore(s.score);
    };
    const interval = setInterval(tick, 150);
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  // Render loop
  useEffect(() => {
    const render = () => {
      const canvas = canvasRef.current;
      if (!canvas) { animRef.current = requestAnimationFrame(render); return; }
      const ctx = canvas.getContext('2d');
      const s = stateRef.current;
      ctx.fillStyle = '#070301';
      ctx.fillRect(0, 0, PAC_COLS * PAC_CELL, PAC_ROWS * PAC_CELL);
      if (!s) { animRef.current = requestAnimationFrame(render); return; }

      // Draw maze
      for (let r = 0; r < PAC_ROWS; r++) {
        for (let c = 0; c < PAC_COLS; c++) {
          drawMazeCell(ctx, s.maze[r][c], c * PAC_CELL, r * PAC_CELL, s.mouth);
        }
      }

      // Ghost house door
      ctx.fillStyle = '#ff69b4';
      ctx.fillRect(8 * PAC_CELL + 3, 8 * PAC_CELL + PAC_CELL - 2, 3 * PAC_CELL - 6, 2);

      // Draw ghosts
      s.ghosts.forEach(g => {
        if (!g.released) return;
        drawGhost(ctx, g, s.scared > 0, s.scared);
      });

      // Draw pac-man
      drawPacman(ctx, s.pac, s.mouth);

      // Lives indicator
      for (let i = 0; i < s.lives - 1; i++) {
        ctx.fillStyle = '#ffff00';
        ctx.beginPath();
        ctx.moveTo(14 + i * 20, PAC_ROWS * PAC_CELL - 5);
        ctx.arc(14 + i * 20, PAC_ROWS * PAC_CELL - 5, 6, 0.3, Math.PI * 2 - 0.3);
        ctx.closePath();
        ctx.fill();
      }

      // RUA text
      ctx.fillStyle = '#83bf68';
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'right';
      ctx.fillText('RUA', PAC_COLS * PAC_CELL - 8, PAC_ROWS * PAC_CELL - 3);
      ctx.textAlign = 'left';

      animRef.current = requestAnimationFrame(render);
    };
    animRef.current = requestAnimationFrame(render);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div className="flex flex-col items-center gap-4" data-testid="pacman-game">
      <UnreleasedTrackPopup isOpen={showUnlocked} onClose={() => setShowUnlocked(false)} />

      <div className="w-full max-w-md bg-retro-black border-2 border-retro-green p-2">
        <div className="text-center text-retro-green text-sm mb-2 font-bold">PACMAN.EXE</div>
        <div className="flex justify-between items-center px-2">
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-red-500 text-lg">RICO</div>
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-retro-green text-lg">
            {String(score).padStart(5, '0')}
          </div>
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-red-500 text-lg">RUA</div>
        </div>
      </div>

      {!isPlaying && !gameOver && (
        <button onClick={resetGame} className="bg-retro-green text-retro-black px-6 py-2 font-bold uppercase hover:bg-retro-dim-green transition-colors" data-testid="pacman-start">
          START GAME
        </button>
      )}

      {gameOver && (
        <div className="text-center">
          <div className={`text-lg uppercase animate-blink mb-2 ${lives<=0?'text-red-500':'text-retro-green'}`}>
            {lives <= 0 ? `GAME OVER - SCORE: ${score}` : `YOU WIN! - SCORE: ${score}`}
          </div>
          <button onClick={resetGame} className="bg-retro-green text-retro-black px-6 py-2 font-bold uppercase hover:bg-retro-dim-green transition-colors">
            PLAY AGAIN
          </button>
        </div>
      )}

      <div className="border-2 border-retro-green bg-retro-black">
        <canvas ref={canvasRef} width={PAC_COLS*PAC_CELL} height={PAC_ROWS*PAC_CELL} />
      </div>

      {/* Mobile Controls */}
      <div className="md:hidden flex flex-col items-center gap-2 mt-2" style={{ touchAction: 'manipulation' }}>
        <button onClick={() => changeDirection('up')} className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none" data-testid="pacman-btn-up">▲</button>
        <div className="flex gap-2">
          <button onClick={() => changeDirection('left')} className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none" data-testid="pacman-btn-left">◄</button>
          <div className="w-14 h-14" />
          <button onClick={() => changeDirection('right')} className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none" data-testid="pacman-btn-right">►</button>
        </div>
        <button onClick={() => changeDirection('down')} className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none" data-testid="pacman-btn-down">▼</button>
      </div>

      <div className="hidden md:block text-xs text-retro-dim-green text-center">
        <p>USE ARROW KEYS TO MOVE</p>
      </div>
    </div>
  );
};


// Minesweeper Game Component with RANDOM mines
const MinesweeperGame = () => {
  const [showUnlocked, setShowUnlocked] = useState(false);
  
  const createRandomBoard = () => {
    const board = Array(MINE_ROWS).fill(null).map(() => 
      Array(MINE_COLS).fill(null).map(() => ({
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborMines: 0
      }))
    );
    
    let minesPlaced = 0;
    while (minesPlaced < MINE_COUNT) {
      const row = Math.floor(Math.random() * MINE_ROWS);
      const col = Math.floor(Math.random() * MINE_COLS);
      if (!board[row][col].isMine) {
        board[row][col].isMine = true;
        minesPlaced++;
      }
    }
    
    for (let r = 0; r < MINE_ROWS; r++) {
      for (let c = 0; c < MINE_COLS; c++) {
        if (!board[r][c].isMine) {
          let count = 0;
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              const nr = r + dr;
              const nc = c + dc;
              if (nr >= 0 && nr < MINE_ROWS && nc >= 0 && nc < MINE_COLS && board[nr][nc].isMine) {
                count++;
              }
            }
          }
          board[r][c].neighborMines = count;
        }
      }
    }
    
    return board;
  };
  
  const [board, setBoard] = useState(createRandomBoard);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [flagCount, setFlagCount] = useState(0);
  
  const resetGame = () => {
    setBoard(createRandomBoard());
    setGameOver(false);
    setGameWon(false);
    setFlagCount(0);
    setShowUnlocked(false);
  };
  
  const revealCell = (row, col) => {
    if (gameOver || gameWon || board[row][col].isRevealed || board[row][col].isFlagged) return;
    
    const newBoard = [...board.map(r => [...r.map(c => ({...c}))])];
    
    if (newBoard[row][col].isMine) {
      for (let r = 0; r < MINE_ROWS; r++) {
        for (let c = 0; c < MINE_COLS; c++) {
          if (newBoard[r][c].isMine) {
            newBoard[r][c].isRevealed = true;
          }
        }
      }
      setBoard(newBoard);
      setGameOver(true);
      setTimeout(() => setShowUnlocked(true), 1000);
      return;
    }
    
    const reveal = (r, c) => {
      if (r < 0 || r >= MINE_ROWS || c < 0 || c >= MINE_COLS) return;
      if (newBoard[r][c].isRevealed || newBoard[r][c].isFlagged || newBoard[r][c].isMine) return;
      newBoard[r][c].isRevealed = true;
      if (newBoard[r][c].neighborMines === 0) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            reveal(r + dr, c + dc);
          }
        }
      }
    };
    
    reveal(row, col);
    setBoard(newBoard);
    
    let allRevealed = true;
    for (let r = 0; r < MINE_ROWS; r++) {
      for (let c = 0; c < MINE_COLS; c++) {
        if (!newBoard[r][c].isMine && !newBoard[r][c].isRevealed) {
          allRevealed = false;
          break;
        }
      }
    }
    if (allRevealed) {
      setGameWon(true);
      setTimeout(() => setShowUnlocked(true), 1000);
    }
  };
  
  const toggleFlag = (e, row, col) => {
    e.preventDefault();
    if (gameOver || gameWon || board[row][col].isRevealed) return;
    
    const newBoard = [...board.map(r => [...r.map(c => ({...c}))])];
    newBoard[row][col].isFlagged = !newBoard[row][col].isFlagged;
    setBoard(newBoard);
    setFlagCount(prev => newBoard[row][col].isFlagged ? prev + 1 : prev - 1);
  };
  
  const getNumberColor = (num) => {
    const colors = { 1: 'text-blue-400', 2: 'text-green-400', 3: 'text-red-400', 4: 'text-purple-400', 5: 'text-yellow-400', 6: 'text-cyan-400', 7: 'text-white', 8: 'text-gray-400' };
    return colors[num] || '';
  };
  
  return (
    <div className="flex flex-col items-center gap-4" data-testid="minesweeper-game">
      <UnreleasedTrackPopup isOpen={showUnlocked} onClose={() => setShowUnlocked(false)} />
      
      <div className="w-full max-w-md bg-retro-black border-2 border-retro-green p-2">
        <div className="text-center text-retro-green text-sm mb-2 font-bold">IL_SENSO_DEL_CAOS</div>
        <div className="flex justify-between items-center px-2">
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-red-500 text-lg">RICO</div>
          <button onClick={resetGame} className="w-10 h-10 bg-retro-green text-retro-black text-xl flex items-center justify-center hover:bg-retro-dim-green transition-colors" data-testid="reset-game">
            {getMinesweeperFace(gameOver, gameWon)}
          </button>
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-red-500 text-lg">RUA</div>
        </div>
      </div>
      
      {gameOver && <div className="text-red-500 text-lg uppercase animate-blink">GAME OVER</div>}
      {gameWon && <div className="text-retro-green text-lg uppercase animate-blink">YOU WIN!</div>}
      
      <div className="border-2 border-retro-green bg-retro-black p-1">
        <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${MINE_COLS}, 20px)`, gridTemplateRows: `repeat(${MINE_ROWS}, 20px)` }}>
          {board.map((row, rowIdx) => 
            row.map((cell, colIdx) => (
              <button
                key={`${rowIdx}-${colIdx}`}
                onClick={() => revealCell(rowIdx, colIdx)}
                onContextMenu={(e) => toggleFlag(e, rowIdx, colIdx)}
                className={`w-5 h-5 text-xs font-bold flex items-center justify-center border ${cell.isRevealed ? 'bg-retro-black border-retro-dim-green' : 'bg-retro-dim-green border-retro-green hover:bg-retro-green hover:bg-opacity-50'} ${cell.isMine && cell.isRevealed ? 'bg-red-900' : ''}`}
                disabled={gameOver || gameWon}
              >
                {getMinesweeperCellContent(cell, getNumberColor)}
              </button>
            ))
          )}
        </div>
      </div>
      
      <div className="text-xs text-retro-dim-green text-center">
        <p>LEFT CLICK: Reveal | RIGHT CLICK: Flag</p>
      </div>
    </div>
  );
};


// Snake Game Component
const SnakeGame = () => {
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState({ x: 15, y: 15 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUnlocked, setShowUnlocked] = useState(false);
  
  const directionRef = useRef({ x: 1, y: 0 });
  const snakeRef = useRef([{ x: 10, y: 10 }]);
  const foodRef = useRef({ x: 15, y: 15 });
  const gameOverRef = useRef(false);
  const isPlayingRef = useRef(false);
  const scoreRef = useRef(0);
  
  const generateFood = useCallback((currentSnake) => {
    let newFood;
    do {
      newFood = {
        x: Math.floor(Math.random() * SNAKE_GRID_SIZE),
        y: Math.floor(Math.random() * SNAKE_GRID_SIZE)
      };
    } while (currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }, []);
  
  const resetGame = useCallback(() => {
    const initialSnake = [{ x: 10, y: 10 }];
    const initialFood = generateFood(initialSnake);
    snakeRef.current = initialSnake;
    foodRef.current = initialFood;
    directionRef.current = { x: 1, y: 0 };
    gameOverRef.current = false;
    isPlayingRef.current = true;
    scoreRef.current = 0;
    setSnake(initialSnake);
    setFood(initialFood);
    setGameOver(false);
    setScore(0);
    setIsPlaying(true);
    setShowUnlocked(false);
  }, [generateFood]);
  
  const changeDirection = useCallback((newDir) => {
    if (!isPlayingRef.current) return;
    const d = directionRef.current;
    if (newDir === 'up' && d.y !== 1) directionRef.current = { x: 0, y: -1 };
    if (newDir === 'down' && d.y !== -1) directionRef.current = { x: 0, y: 1 };
    if (newDir === 'left' && d.x !== 1) directionRef.current = { x: -1, y: 0 };
    if (newDir === 'right' && d.x !== -1) directionRef.current = { x: 1, y: 0 };
  }, []);
  
  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'ArrowUp': changeDirection('up'); break;
        case 'ArrowDown': changeDirection('down'); break;
        case 'ArrowLeft': changeDirection('left'); break;
        case 'ArrowRight': changeDirection('right'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [changeDirection]);
  
  useEffect(() => {
    if (!isPlaying || gameOver) return;
    
    const moveSnake = () => {
      if (gameOverRef.current || !isPlayingRef.current) return;
      
      const currentSnake = snakeRef.current;
      const dir = directionRef.current;
      const currentFood = foodRef.current;
      
      const newHead = {
        x: currentSnake[0].x + dir.x,
        y: currentSnake[0].y + dir.y
      };
      
      if (newHead.x < 0 || newHead.x >= SNAKE_GRID_SIZE || newHead.y < 0 || newHead.y >= SNAKE_GRID_SIZE) {
        gameOverRef.current = true;
        isPlayingRef.current = false;
        setGameOver(true);
        setIsPlaying(false);
        setTimeout(() => setShowUnlocked(true), 1000);
        return;
      }
      
      if (currentSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
        gameOverRef.current = true;
        isPlayingRef.current = false;
        setGameOver(true);
        setIsPlaying(false);
        setTimeout(() => setShowUnlocked(true), 1000);
        return;
      }
      
      const newSnake = [newHead, ...currentSnake];
      
      if (newHead.x === currentFood.x && newHead.y === currentFood.y) {
        scoreRef.current += 10;
        const newFood = generateFood(newSnake);
        foodRef.current = newFood;
        setScore(scoreRef.current);
        setFood(newFood);
      } else {
        newSnake.pop();
      }
      
      snakeRef.current = newSnake;
      setSnake([...newSnake]);
    };
    
    const gameInterval = setInterval(moveSnake, SNAKE_SPEED);
    return () => clearInterval(gameInterval);
  }, [isPlaying, gameOver, generateFood]);
  
  return (
    <div className="flex flex-col items-center gap-4" data-testid="snake-game">
      <UnreleasedTrackPopup isOpen={showUnlocked} onClose={() => setShowUnlocked(false)} />
      
      <div className="w-full max-w-md bg-retro-black border-2 border-retro-green p-2">
        <div className="text-center text-retro-green text-sm mb-2 font-bold">SNAKE.EXE</div>
        <div className="flex justify-between items-center px-2">
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-red-500 text-lg">RICO</div>
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-retro-green text-lg">
            {String(score).padStart(4, '0')}
          </div>
          <div className="bg-retro-black border border-retro-green px-3 py-1 font-mono text-red-500 text-lg">RUA</div>
        </div>
      </div>
      
      {!isPlaying && !gameOver && (
        <button onClick={resetGame} className="bg-retro-green text-retro-black px-6 py-2 font-bold uppercase hover:bg-retro-dim-green transition-colors">
          START GAME
        </button>
      )}
      
      {gameOver && (
        <div className="text-center">
          <div className="text-red-500 text-lg uppercase animate-blink mb-2">GAME OVER - SCORE: {score}</div>
          <button onClick={resetGame} className="bg-retro-green text-retro-black px-6 py-2 font-bold uppercase hover:bg-retro-dim-green transition-colors">
            PLAY AGAIN
          </button>
        </div>
      )}
      
      <div 
        className="border-2 border-retro-green bg-retro-black relative"
        style={{ width: SNAKE_GRID_SIZE * SNAKE_CELL_SIZE, height: SNAKE_GRID_SIZE * SNAKE_CELL_SIZE }}
      >
        {snake.map((segment, index) => (
          <div
            key={`${segment.x}-${segment.y}`}
            className={`absolute ${index === 0 ? 'bg-retro-green' : 'bg-retro-dim-green'}`}
            style={{
              left: segment.x * SNAKE_CELL_SIZE,
              top: segment.y * SNAKE_CELL_SIZE,
              width: SNAKE_CELL_SIZE - 1,
              height: SNAKE_CELL_SIZE - 1,
            }}
          />
        ))}
        
        <div
          className="absolute bg-red-500"
          style={{
            left: food.x * SNAKE_CELL_SIZE,
            top: food.y * SNAKE_CELL_SIZE,
            width: SNAKE_CELL_SIZE - 1,
            height: SNAKE_CELL_SIZE - 1,
          }}
        />
      </div>
      
      {/* Mobile Controls - only visible on mobile */}
      <div className="md:hidden flex flex-col items-center gap-2 mt-4" style={{ touchAction: 'manipulation' }}>
        <button
          onClick={() => changeDirection('up')}
          className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none"
          data-testid="snake-btn-up"
        >
          ▲
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => changeDirection('left')}
            className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none"
            data-testid="snake-btn-left"
          >
            ◄
          </button>
          <div className="w-14 h-14"></div>
          <button
            onClick={() => changeDirection('right')}
            className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none"
            data-testid="snake-btn-right"
          >
            ►
          </button>
        </div>
        <button
          onClick={() => changeDirection('down')}
          className="w-14 h-14 bg-retro-green text-retro-black font-bold text-2xl flex items-center justify-center active:bg-retro-dim-green select-none"
          data-testid="snake-btn-down"
        >
          ▼
        </button>
      </div>
      
      {/* Desktop instruction - only visible on desktop */}
      <div className="hidden md:block text-xs text-retro-dim-green text-center">
        <p>USE ARROW KEYS TO MOVE</p>
      </div>
    </div>
  );
};


// Games Page Content with Menu
const GamesPage = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  
  if (selectedGame === 'pacman') {
    return (
      <div className="space-y-6 text-retro-green font-matrix" data-testid="games-page">
        <button
          onClick={() => setSelectedGame(null)}
          className="text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
        >
          &lt; BACK TO GAMES
        </button>
        <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4">
          PACMAN.EXE
        </h2>
        <PacmanGame />
      </div>
    );
  }
  
  if (selectedGame === 'minesweeper') {
    return (
      <div className="space-y-6 text-retro-green font-matrix" data-testid="games-page">
        <button
          onClick={() => setSelectedGame(null)}
          className="text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
        >
          &lt; BACK TO GAMES
        </button>
        <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4">
          PRATO_FIORITO.EXE
        </h2>
        <MinesweeperGame />
      </div>
    );
  }
  
  if (selectedGame === 'snake') {
    return (
      <div className="space-y-6 text-retro-green font-matrix" data-testid="games-page">
        <button
          onClick={() => setSelectedGame(null)}
          className="text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
        >
          &lt; BACK TO GAMES
        </button>
        <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4">
          SNAKE.EXE
        </h2>
        <SnakeGame />
      </div>
    );
  }
  
  return (
    <div className="space-y-6 text-retro-green font-matrix" data-testid="games-page">
      <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 glitch-text" data-text="GAMES">
        GAMES
      </h2>
      
      <div className="border-2 border-retro-green p-4 mb-6 bg-retro-black">
        <p className="text-sm text-retro-green uppercase">
          &gt; GIOCA PER SBLOCCARE UNA TRACCIA UNRELEASED_
        </p>
        <p className="text-xs text-retro-dim-green mt-2">
          Completa uno dei giochi per accedere a contenuti esclusivi mai pubblicati
        </p>
      </div>
      
      <p className="text-sm text-retro-dim-green uppercase mb-4">
        &gt; SELECT PROGRAM_
      </p>
      
      <div className="space-y-2">
        <button
          onClick={() => setSelectedGame('pacman')}
          className="flex items-center gap-3 p-3 w-full text-left uppercase tracking-wider text-base md:text-lg transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black border border-retro-green"
          data-testid="select-pacman"
        >
          <span className="text-retro-dim-green">&gt;</span>
          <span className="font-bold">PACMAN.EXE</span>
        </button>
        
        <button
          onClick={() => setSelectedGame('minesweeper')}
          className="flex items-center gap-3 p-3 w-full text-left uppercase tracking-wider text-base md:text-lg transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black border border-retro-green"
          data-testid="select-minesweeper"
        >
          <span className="text-retro-dim-green">&gt;</span>
          <span className="font-bold">PRATO_FIORITO.EXE</span>
        </button>
        
        <button
          onClick={() => setSelectedGame('snake')}
          className="flex items-center gap-3 p-3 w-full text-left uppercase tracking-wider text-base md:text-lg transition-colors text-retro-green hover:bg-retro-green hover:text-retro-black border border-retro-green"
          data-testid="select-snake"
        >
          <span className="text-retro-dim-green">&gt;</span>
          <span className="font-bold">SNAKE.EXE</span>
        </button>
      </div>
    </div>
  );
};


// Main App Component
function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [isIntroFading, setIsIntroFading] = useState(false);
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('menu');

  const handleIntroEnd = () => {
    if (isIntroFading) return;
    setIsIntroFading(true);
    setTimeout(() => setShowIntro(false), 800);
  };

  useEffect(() => {
    if (!showIntro) return;
    const fallback = setTimeout(() => handleIntroEnd(), 8000);
    return () => clearTimeout(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIntro]);

  const handleFolderClick = () => {
    setIsWindowOpen(true);
    setCurrentPage('menu');
  };

  const handleCloseWindow = () => {
    setIsWindowOpen(false);
    setCurrentPage('menu');
  };

  const handleMenuClick = (page) => {
    setCurrentPage(page);
  };

  const handleBackToMenu = () => {
    setCurrentPage('menu');
  };

  const renderContent = () => {
    switch (currentPage) {
      case 'concept':
        return (
          <div>
            <button
              onClick={handleBackToMenu}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-menu">

              &lt; BACK TO MENU
            </button>
            <ConceptPage />
          </div>);

      case 'audio':
        return (
          <div>
            <button
              onClick={handleBackToMenu}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-menu">

              &lt; BACK TO MENU
            </button>
            <AudioPage />
          </div>);

      case 'merchandising':
        return (
          <div>
            <button
              onClick={handleBackToMenu}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-menu">

              &lt; BACK TO MENU
            </button>
            <MerchandisingPage />
          </div>);

      case 'contact':
        return (
          <div>
            <button
              onClick={handleBackToMenu}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-menu">

              &lt; BACK TO MENU
            </button>
            <ContactPage />
          </div>);

      case 'extra':
        return (
          <div>
            <button
              onClick={handleBackToMenu}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-menu">

              &lt; BACK TO MENU
            </button>
            <ExtraPage onNavigate={handleMenuClick} />
          </div>);

      case 'artworks':
        return (
          <div>
            <button
              onClick={() => handleMenuClick('extra')}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-extra">

              &lt; BACK TO EXTRA
            </button>
            <ArtworksPage />
          </div>);

      case 'download':
        return (
          <div>
            <button
              onClick={() => handleMenuClick('extra')}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-extra">

              &lt; BACK TO EXTRA
            </button>
            <DownloadPage />
          </div>);

      case 'content':
        return (
          <div>
            <button
              onClick={() => handleMenuClick('extra')}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-extra">

              &lt; BACK TO EXTRA
            </button>
            <ContentPage />
          </div>);

      case 'games':
        return (
          <div>
            <button
              onClick={() => handleMenuClick('extra')}
              className="mb-6 text-sm uppercase tracking-wide hover:bg-retro-green hover:text-retro-black px-2 py-1 transition-colors"
              data-testid="back-to-extra">

              &lt; BACK TO EXTRA
            </button>
            <GamesPage />
          </div>);

      default:
        return (
          <div className="space-y-4" data-testid="menu-page">
            <h2 className="text-xl md:text-2xl font-bold uppercase tracking-widest border-b-2 border-retro-green pb-4 mb-6">
              &gt; SELECT FOLDER_
            </h2>
            <MenuFolder name="Concept" onClick={() => handleMenuClick('concept')} />
            <MenuFolder name="Audio" onClick={() => handleMenuClick('audio')} />
            <MenuFolder name="Merchandising" onClick={() => handleMenuClick('merchandising')} />
            <MenuFolder name="Extra" onClick={() => handleMenuClick('extra')} />
            <MenuFolder name="Contact" onClick={() => handleMenuClick('contact')} />
          </div>);

    }
  };

  return (
    <div className="App bg-retro-black min-h-screen relative crt">
      <Scanlines />

      {/* Intro Video Overlay */}
      {showIntro && (
        <div
          className="fixed inset-0 z-50 bg-retro-black flex items-center justify-center"
          style={{
            opacity: isIntroFading ? 0 : 1,
            transition: 'opacity 800ms ease-out',
            pointerEvents: isIntroFading ? 'none' : 'auto',
          }}
          data-testid="intro-overlay"
        >
          <video
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={handleIntroEnd}
            className="w-full h-full object-contain"
            data-testid="intro-video"
          >
            <source src="/assets/intro.webm" type="video/webm" />
            <source src="/assets/intro.mp4" type="video/mp4" />
          </video>
          <button
            onClick={handleIntroEnd}
            className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-10 px-3 py-1 md:px-4 md:py-2 border-2 border-retro-green bg-retro-black text-retro-green hover:bg-retro-green hover:text-retro-black transition-colors uppercase tracking-widest text-xs md:text-sm font-matrix font-bold"
            data-testid="skip-intro"
            aria-label="Skip intro"
          >
            SKIP_
          </button>
        </div>
      )}

      {/* Home Screen - hidden when window is open */}
      {!isWindowOpen &&
      <div className="flex items-center justify-center min-h-screen animate-fade-in">
          <DesktopFolder onClick={handleFolderClick} />
        </div>
      }
      
      {/* Retro Window */}
      <RetroWindow
        isOpen={isWindowOpen}
        onClose={handleCloseWindow}
        title="Il Senso Del Caos">

        {renderContent()}
      </RetroWindow>
    </div>);

}

export default App;